// Onboarding gate
// ---------------
// Onboarding asks for a main photo and a 15-second intro video, and the app is
// built on the assumption that every member has both: the swipe card, all four
// feeds, Explore's grid and the profile drawer are made of them. An account
// that gets past that step without them finishes onboarding invisible — it
// appears in nobody's deck with a face, and the person has no way to tell that
// is why nothing is happening.
//
// Removing the Skip button stops the obvious way through. This stops the rest:
// typing a URL, a bookmark, a stale tab, the browser's back button, any link
// that fires before the flow is finished. There is no exit that lands anywhere
// other than back in the flow.
//
// What it deliberately does NOT do is trap anyone. It redirects only when it
// positively knows onboarding is unfinished for a signed-in account. Not
// knowing — still loading, signed out, the check failed — means letting the
// navigation through. A gate whose failure mode is a locked door is worse than
// the hole it closes.

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useOnboardingStatus } from '../../hooks/onboarding/useOnboardingStatus';
import { isExemptFromOnboarding } from './onboardingExemptRoutes';

export const OnboardingGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const status = useOnboardingStatus();

  if (isExemptFromOnboarding(location.pathname)) return <>{children}</>;

  // 'unknown' covers loading, signed out and a failed check. All three mean
  // "no evidence this account is unfinished", which is not grounds to redirect.
  if (status !== 'incomplete') return <>{children}</>;

  return <Navigate to="/onboarding" replace />;
};
